import {
  CloseOutlined,
  CustomerServiceOutlined,
  MailOutlined,
  PhoneOutlined,
  QqOutlined,
  ScheduleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { history } from '@umijs/max';
import { Button, Image, Typography } from 'antd';
import React, { useState } from 'react';
import type { SupportConfigResponse } from '@/services/support-config';

const { Text, Paragraph } = Typography;

interface CustomerServiceFloatProps {
  config: SupportConfigResponse;
}

const CustomerServiceFloat: React.FC<CustomerServiceFloatProps> = ({
  config,
}) => {
  const [expanded, setExpanded] = useState(false);

  const pathname = window.location.pathname;
  const match = pathname.match(/^\/(cdn-oper|saas-platform)/);
  const platform = match?.[1] || '';

  const ticketPath = platform
    ? `/${platform}/ticket/my-tickets`
    : '/ticket/my-tickets';
  const supportPath = platform
    ? `/${platform}/workbench/support`
    : '/workbench/support';

  if (!expanded) {
    return (
      <div
        onClick={() => setExpanded(true)}
        style={{
          position: 'fixed',
          right: 24,
          bottom: 80,
          width: 48,
          height: 48,
          borderRadius: '50%',
          backgroundColor: '#1890ff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(24, 144, 255, 0.4)',
          zIndex: 1000,
          transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'scale(1.1)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
        }}
      >
        <CustomerServiceOutlined style={{ fontSize: 24, color: '#fff' }} />
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        right: 24,
        bottom: 80,
        width: 320,
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
        zIndex: 1000,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          backgroundColor: '#1890ff',
          padding: '12px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Text strong style={{ color: '#fff', fontSize: 15 }}>
          <CustomerServiceOutlined style={{ marginRight: 8 }} />
          联系客服
        </Text>
        <CloseOutlined
          style={{ color: '#fff', cursor: 'pointer', fontSize: 14 }}
          onClick={() => setExpanded(false)}
        />
      </div>

      <div style={{ padding: 16, maxHeight: 400, overflowY: 'auto' }}>
        {config.working_hours?.enabled && config.working_hours?.text && (
          <Paragraph
            type="secondary"
            style={{ marginBottom: 12, fontSize: 13 }}
          >
            <ScheduleOutlined style={{ marginRight: 6 }} />
            服务时间: {config.working_hours.text}
          </Paragraph>
        )}

        {config.phone?.enabled && config.phone?.number && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              客服电话
            </Text>
            <div>
              <PhoneOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              <Text strong style={{ fontSize: 16 }}>
                {config.phone.number}
              </Text>
            </div>
          </div>
        )}

        {config.email?.enabled && config.email?.address && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              客服邮箱
            </Text>
            <div>
              <MailOutlined style={{ marginRight: 8, color: '#1890ff' }} />
              <Text>{config.email.address}</Text>
            </div>
          </div>
        )}

        {config.wechat?.enabled && config.wechat?.qr_url && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {config.wechat.label || '微信客服'}
            </Text>
            <div>
              <Image
                src={config.wechat.qr_url}
                width={160}
                height={160}
                preview={false}
              />
            </div>
          </div>
        )}

        {config.qq?.enabled && (config.qq?.number || config.qq?.qr_url) && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {config.qq.label || 'QQ客服'}
            </Text>
            {config.qq.number && (
              <div>
                <QqOutlined style={{ marginRight: 8, color: '#12b7f5' }} />
                <Text strong>{config.qq.number}</Text>
              </div>
            )}
            {config.qq.qr_url && (
              <Image
                src={config.qq.qr_url}
                width={160}
                height={160}
                preview={false}
                style={{ marginTop: 4 }}
              />
            )}
          </div>
        )}

        {config.telegram?.enabled &&
          (config.telegram?.link || config.telegram?.qr_url) && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {config.telegram.label || 'Telegram'}
              </Text>
              {config.telegram.link && (
                <div>
                  <SendOutlined style={{ marginRight: 8, color: '#0088cc' }} />
                  <a
                    href={config.telegram.link}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {config.telegram.link}
                  </a>
                </div>
              )}
              {config.telegram.qr_url && (
                <Image
                  src={config.telegram.qr_url}
                  width={160}
                  height={160}
                  preview={false}
                  style={{ marginTop: 4 }}
                />
              )}
            </div>
          )}

        {config.extra_info?.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            {config.extra_info.map((item) => (
              <div key={item.id} style={{ marginBottom: 8 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {item.label}
                </Text>
                <div>
                  <Text>{item.value}</Text>
                </div>
                {item.qr_url && (
                  <Image
                    src={item.qr_url}
                    width={100}
                    preview={false}
                    style={{ marginTop: 4 }}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            borderTop: '1px solid #f0f0f0',
            paddingTop: 12,
            display: 'flex',
            gap: 8,
          }}
        >
          {config.ticket_enabled && (
            <Button
              size="small"
              type="primary"
              ghost
              onClick={() => history.push(ticketPath)}
            >
              <ScheduleOutlined style={{ marginRight: 4 }} />
              提交工单
            </Button>
          )}
          <Button size="small" onClick={() => history.push(supportPath)}>
            查看详情
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CustomerServiceFloat;
